import { Alert, Badge, Group, Paper, Stack, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import StyledButton from '@/components/StyledButton/StyledButton.tsx';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { reportException } from '@/services/sentry.ts';

type ScheduleStatus = {
  trigger_step_id: string;
  activation_id: string;
  automation_version_id: string;
  every_minutes: number;
  anchor_at: string;
  next_due_at: string;
  is_retired: boolean;
  is_paused: boolean;
  paused_at: string | null;
  pause_reason: string;
  last_scheduled_for: string | null;
  last_status: 'started' | 'skipped_overlap' | null;
  last_skipped_intervals: number;
  last_run_id: string | null;
};

export default function ScheduledTriggers({ automationId }: { automationId: string }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const queryKey = ['automation-schedules', automationId];
  const schedules = useQuery({
    queryKey,
    queryFn: async () =>
      (await api.get<ScheduleStatus[]>(`/automations/${automationId}/schedules`)).data,
    refetchInterval: 60_000,
  });
  // Equal payloads retain their data reference, but every successful poll must
  // reconsider whether an unchanged due time has become overdue.
  const observedAt = schedules.dataUpdatedAt;
  const update = useMutation({
    mutationFn: async ({ schedule, paused }: { schedule: ScheduleStatus; paused: boolean }) =>
      (
        await api.post<ScheduleStatus>(
          `/automations/${automationId}/schedules/${schedule.trigger_step_id}/pause`,
          { expected_activation_id: schedule.activation_id, paused },
        )
      ).data,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey }),
    onError: (error) => {
      reportException(error);
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  if (schedules.isError) {
    return (
      <Alert color="red" mt="md">
        Schedule status could not be loaded.
      </Alert>
    );
  }
  if (!schedules.data?.length) return null;
  return (
    <Stack gap="xs" mt="md">
      <Text fw={600} size="sm">
        Schedules
      </Text>
      {update.isError && (
        <Alert color="red">Could not update the schedule. Its status has been refreshed.</Alert>
      )}
      {schedules.data.map((schedule) => {
        const isOverdue =
          !schedule.is_retired &&
          !schedule.is_paused &&
          new Date(schedule.next_due_at).getTime() < observedAt - 15 * 60_000;
        return (
          <Paper key={schedule.trigger_step_id} withBorder p="sm" radius="md">
            <Group justify="space-between" align="center" wrap="wrap" gap="sm">
              <Stack gap={2}>
                <Group gap="xs">
                  <Text fw={500} size="sm">
                    {schedule.trigger_step_id}
                  </Text>
                  <Badge
                    size="sm"
                    color={
                      schedule.is_retired || schedule.is_paused
                        ? 'gray'
                        : isOverdue
                          ? 'orange'
                          : 'green'
                    }
                  >
                    {schedule.is_retired
                      ? 'Retired'
                      : schedule.is_paused
                        ? 'Paused'
                        : isOverdue
                          ? 'Overdue'
                          : 'Active'}
                  </Badge>
                </Group>
                <Text size="xs" c="dimmed">
                  Every {schedule.every_minutes} minutes · UTC anchor{' '}
                  {new Date(schedule.anchor_at).toISOString()}
                </Text>
                <Text size="xs" c="dimmed">
                  {schedule.is_retired
                    ? 'Suspended while this automation is retired'
                    : schedule.is_paused
                      ? 'Paused'
                      : `Next due ${new Date(schedule.next_due_at).toISOString()}`}
                  {isOverdue ? ' · Dispatch is more than 15 minutes late' : ''}
                  {schedule.last_scheduled_for
                    ? ` · Last ${schedule.last_status?.replace('_', ' ')} ${new Date(
                        schedule.last_scheduled_for,
                      ).toISOString()}`
                    : ' · No firings yet'}
                  {schedule.last_skipped_intervals > 0
                    ? ` · ${schedule.last_skipped_intervals} missed interval${
                        schedule.last_skipped_intervals === 1 ? '' : 's'
                      }`
                    : ''}
                </Text>
              </Stack>
              {!schedule.is_retired && (
                <StyledButton
                  size="sm"
                  variant="outline"
                  loading={update.isPending}
                  onClick={() => update.mutate({ schedule, paused: !schedule.is_paused })}
                >
                  {schedule.is_paused ? 'Resume' : 'Pause'}
                </StyledButton>
              )}
            </Group>
          </Paper>
        );
      })}
    </Stack>
  );
}
